// Simple analytics tracking utilities with Follow Up Boss integration
import { trackFormSubmission } from './gtmTracking';

export interface LeadData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  message?: string;
  source: string;
  leadType: 'info_request' | 'recruiter_contact' | 'application';
  timestamp: string;
  // Additional application fields
  address?: string;
  city?: string;
  county?: string;
  state?: string;
  zipCode?: string;
  currentEmployment?: string;
  occupation?: string;
  yearsExperience?: string;
  previousSales?: string;
  realEstateExperience?: string;
  huntingExperience?: string[];
  farmingExperience?: string[];
  landRelatedExperience?: string;
  whyKiloterra?: string;
  heardAboutKiloterra?: string;
  readinessStatus?: string;
  additionalInfo?: string;
}

// Follow Up Boss API configuration
const FUB_API_BASE = 'https://api.followupboss.com/v1';
const FUB_API_KEY = import.meta.env.VITE_FUB_API_KEY;

// Format comprehensive lead message based on lead type
const formatLeadMessage = (leadData: LeadData): string => {
  const timestamp = new Date(leadData.timestamp).toLocaleString();
  
  // Debug logging to see what message data we have
  console.log('🔍 Formatting message for lead type:', leadData.leadType);
  console.log('🔍 Message content:', leadData.message);
  console.log('🔍 Full lead data:', leadData);
  
  switch (leadData.leadType) {
    case 'info_request':
      return `KILOTERRA Career Information Request

Contact Information:
Name: ${leadData.firstName} ${leadData.lastName}
Email: ${leadData.email}
Phone: ${leadData.phone || 'Not provided'}

Request Details:
• Requested career information packet about KILOTERRA land sales opportunities
• Source: ${leadData.source}
• Submitted: ${timestamp}

Follow-up Action:
Send comprehensive career information packet including:
- FAQ document
- Startup process outline  
- Equipment checklist
- Career benefits overview
- Training program details

${leadData.message ? `\nAdditional Notes:\n${leadData.message}` : ''}`;

    case 'recruiter_contact':
      return `KILOTERRA Recruiter Contact Request

Contact Information:
Name: ${leadData.firstName} ${leadData.lastName}
Email: ${leadData.email}
Phone: ${leadData.phone || 'Not provided'}

Request Details:
• Requested callback to discuss KILOTERRA career opportunities
• Source: ${leadData.source}
• Submitted: ${timestamp}

Follow-up Action:
Schedule phone consultation to discuss:
- Career goals and background
- KILOTERRA opportunity details
- Next steps in application process
- Answer specific questions

${leadData.message ? `\nSpecific Questions/Notes:\n${leadData.message}` : '\nNo specific questions provided.'}`;

    case 'application':
      return `KILOTERRA Career Application Submitted

CONTACT INFORMATION:
Name: ${leadData.firstName} ${leadData.lastName}
Email: ${leadData.email}
Phone: ${leadData.phone || 'Not provided'}
${leadData.address ? `Address: ${leadData.address}, ${leadData.city}, ${leadData.county}, ${leadData.state} ${leadData.zipCode}` : ''}

PROFESSIONAL BACKGROUND:
${leadData.currentEmployment ? `Current Employment: ${leadData.currentEmployment}` : ''}
${leadData.occupation ? `Occupation: ${leadData.occupation}` : ''}
${leadData.yearsExperience ? `Years of Experience: ${leadData.yearsExperience}` : ''}
${leadData.previousSales ? `Previous Sales Experience: ${leadData.previousSales}` : ''}
${leadData.realEstateExperience ? `Real Estate Experience: ${leadData.realEstateExperience}` : ''}

LAND & OUTDOOR EXPERIENCE:
${leadData.huntingExperience && leadData.huntingExperience.length > 0 ? `Hunting Experience: ${leadData.huntingExperience.join(', ')}` : ''}
${leadData.farmingExperience && leadData.farmingExperience.length > 0 ? `Farming Experience: ${leadData.farmingExperience.join(', ')}` : ''}
${leadData.landRelatedExperience ? `Land Related Experience: ${leadData.landRelatedExperience}` : ''}

MOTIVATION & GOALS:
${leadData.whyKiloterra ? `Why KILOTERRA: ${leadData.whyKiloterra}` : ''}
${leadData.heardAboutKiloterra ? `How they heard about us: ${leadData.heardAboutKiloterra}` : ''}
${leadData.readinessStatus ? `Readiness Status: ${leadData.readinessStatus}` : ''}

APPLICATION DETAILS:
• Source: ${leadData.source}
• Submitted: ${timestamp}

FOLLOW-UP ACTION:
1. Review application within 3-5 business days
2. Conduct initial phone screening if qualified
3. Schedule in-person or video interview
4. Provide next steps in hiring process

${leadData.additionalInfo ? `\nAdditional Information:\n${leadData.additionalInfo}` : ''}`;

    default:
      return `KILOTERRA Lead Submission

Contact: ${leadData.firstName} ${leadData.lastName}
Email: ${leadData.email}
Phone: ${leadData.phone || 'Not provided'}
Source: ${leadData.source}
Type: ${leadData.leadType}
Submitted: ${timestamp}

${leadData.message || 'No additional message provided.'}`;
  }
};

// Create event in Follow Up Boss using V1/events API
const createEventInFUB = async (leadData: LeadData) => {
  if (!FUB_API_KEY) {
    console.warn('⚠️ FUB API key not configured');
    return null;
  }

  // Use the firstName and lastName exactly as provided from the form
  const firstName = leadData.firstName.trim();
  const lastName = leadData.lastName.trim();

  // Ensure we have at least a first name
  if (!firstName) {
    throw new Error('First name is required');
  }

  // Build event data for Follow Up Boss - simplified without note
  const eventData: any = {
    type: "General Inquiry",
    person: {
      firstName: firstName,
      lastName: lastName || '',
      emails: [{ value: leadData.email }],
      phones: leadData.phone ? [{ value: leadData.phone }] : []
    },
    source: leadData.source
  };

  // Log the final payload being sent to FUB
  console.log('📤 FUB Events API payload:', JSON.stringify(eventData, null, 2));

  try {
    const response = await fetch(`${FUB_API_BASE}/events`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(FUB_API_KEY + ':')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData)
    });

    // Log response details for debugging
    console.log('📥 FUB Events Response Status:', response.status, response.statusText);

    if (!response.ok) {
      const text = await response.text();
      console.error('❌ FUB Events API Error Response:', text);
      throw new Error(`FUB Events API error: ${response.status} - ${text}`);
    }

    const result = await response.json();
    console.log('✅ Event created in FUB:', result);
    
    return result;
  } catch (error) {
    console.error('❌ Failed to create event in FUB:', error);
    throw error;
  }
};

// Create a separate note in Follow Up Boss
const createNoteInFUB = async (personId: string, leadData: LeadData) => {
  if (!FUB_API_KEY) {
    console.warn('⚠️ FUB API key not configured for notes');
    return null;
  }

  const noteContent = formatLeadMessage(leadData);
  
  // Try different field names that Follow Up Boss might expect
  const noteData = {
    personId: personId,
    body: noteContent  // Changed from 'note' to 'body'
  };

  console.log('📝 Creating note in FUB for person:', personId);
  console.log('📝 Note content preview:', noteContent.substring(0, 200) + '...');
  console.log('📤 FUB Notes API payload:', JSON.stringify(noteData, null, 2));

  try {
    const response = await fetch(`${FUB_API_BASE}/notes`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(FUB_API_KEY + ':')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(noteData)
    });

    console.log('📥 FUB Notes Response Status:', response.status, response.statusText);

    if (!response.ok) {
      const text = await response.text();
      console.error('❌ FUB Notes API Error Response:', text);
      
      // If 'body' doesn't work, try 'content'
      if (response.status === 400 && text.includes('body')) {
        console.log('🔄 Trying with "content" field instead...');
        
        const alternateNoteData = {
          personId: personId,
          content: noteContent
        };
        
        const retryResponse = await fetch(`${FUB_API_BASE}/notes`, {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${btoa(FUB_API_KEY + ':')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(alternateNoteData)
        });
        
        if (!retryResponse.ok) {
          const retryText = await retryResponse.text();
          console.error('❌ FUB Notes API Retry Error:', retryText);
          throw new Error(`FUB Notes API error: ${retryResponse.status} - ${retryText}`);
        }
        
        const retryResult = await retryResponse.json();
        console.log('✅ Note created in FUB (retry):', retryResult);
        return retryResult;
      }
      
      throw new Error(`FUB Notes API error: ${response.status} - ${text}`);
    }

    const result = await response.json();
    console.log('✅ Note created in FUB:', result);
    
    return result;
  } catch (error) {
    console.error('❌ Failed to create note in FUB:', error);
    throw error;
  }
};

// Main form submission handler
export const submitLeadToFollowUpBoss = async (leadData: LeadData): Promise<boolean> => {
  try {
    console.log('🚀 Starting Follow Up Boss event submission...');
    console.log('📋 Lead Data:', {
      name: `${leadData.firstName} ${leadData.lastName}`,
      email: leadData.email,
      type: leadData.leadType,
      source: leadData.source,
      hasMessage: !!leadData.message,
      messagePreview: leadData.message ? leadData.message.substring(0, 50) + '...' : 'No message'
    });
    
    // Create event in Follow Up Boss
    const event = await createEventInFUB(leadData);
    
    if (!event) {
      console.error('❌ Failed to create event - no response returned');
      throw new Error('Event creation failed');
    }

    console.log('✅ Event created successfully:', event.id || 'Event ID not provided');

    // Create a separate note with the detailed message
    if (event.id) {
      console.log('📝 Creating detailed note for event:', event.id);
      try {
        const note = await createNoteInFUB(event.id.toString(), leadData);
        
        if (note) {
          console.log('✅ Note created successfully:', note.id || 'Note ID not provided');
        } else {
          console.warn('⚠️ Note creation failed, but event was created successfully');
        }
      } catch (noteError) {
        console.warn('⚠️ Note creation failed, but event was created successfully:', noteError);
        // Don't fail the entire submission if just the note fails
      }
    } else {
      console.warn('⚠️ No event ID returned, cannot create note');
    }

    // Track analytics (both existing GA4 and new GTM)
    trackCRMSubmission(leadData, true);
    
    console.log('✅ Follow Up Boss event submission completed successfully');
    return true;
    
  } catch (error) {
    console.error('❌ Follow Up Boss event submission failed:', error);
    trackCRMSubmission(leadData, false);
    throw new Error('Follow Up Boss event submission failed');
  }
};

// Enhanced analytics tracking for form submissions (now includes GTM)
export const trackCRMSubmission = (leadData: LeadData, success: boolean) => {
  // Google Analytics 4 (existing)
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'form_submission', {
      lead_type: leadData.leadType,
      source: leadData.source,
      success: success,
      timestamp: leadData.timestamp
    });
  }

  // GTM tracking (new)
  trackFormSubmission(leadData.leadType, success, leadData.source);

  // Console log for debugging
  console.log('📊 Form Submission Tracked:', {
    leadType: leadData.leadType,
    source: leadData.source,
    success: success,
    timestamp: leadData.timestamp
  });
};